import { z } from "zod";
import { hasDisallowedPersonNameChars, normalizePersonNameInput, SUPPORTED_LOCALE_BUNDLE, type BundleLocale } from "@goppy/domain";

/**
 * Shared DTO cho hồ sơ người dùng hiện tại — GET|PATCH /v1/me (US-E2.F7-0014 / @UC0001).
 *
 * Một nguồn sự thật cho api (validate biên + shape response) và mobile (gọi + map),
 * tránh drift (DRY). C5: schema validate tại biên tái dùng được.
 *
 * Cross-check API_BUILD_PLAN [đã xoá → TDD §26] Phase 2: path canonical = `/v1/me` (KHÔNG /v1/users/me).
 * Slice 0014 = TỐI THIỂU: chỉ profile cơ bản + cờ onboardingCompleted. 0015/0016 bồi đắp.
 *
 * Bảo mật (C2/C3): response KHÔNG mang passwordHash/secret/PII thừa — chỉ field cần cho UI.
 */

/**
 * Shape response cho GET /v1/me và PATCH /v1/me.
 * email/fullName/avatarUrl/emailVerifiedAt NULLABLE (user social chưa có email; chưa verify).
 * onboardingCompleted = cờ đã xem onboarding lần đầu chưa (nguồn sự thật server — SD-4).
 */
/**
 * US-E24.F57-0520 (@UC9169) — locale DẪN XUẤT từ `SUPPORTED_LOCALE_BUNDLE` của `@goppy/domain`.
 *
 * 🔴 KHÔNG gõ tay `z.enum(["vi","en"])`. Repo đã trả giá cho DRIFT HAI WHITELIST (TX_TYPES ở domain
 * vs CHECK ở migration): hai bản kê khai cùng một tập, sửa một bên là bên kia mục im lặng. Ở đây,
 * thêm ngôn ngữ thứ ba vào bundle sẽ tự động mở ở biên HTTP; nếu chép tay thì ngôn ngữ mới bị từ
 * chối 400 mà không ai hiểu vì sao. (Bản kê khai thứ hai KHÔNG tránh được là CHECK trong migration —
 * SQL không import được TS; chỗ đó có comment chỉ chéo.)
 */
// `as [BundleLocale, ...BundleLocale[]]` (KHÔNG `as unknown as [string, ...]`): bản cũ làm `z.infer`
// rơi về `string`, nên `MeResponse["locale"]` mất tính đóng và consumer TS không còn kiểm vét cạn
// được. Runtime vẫn đóng ở cả hai bản — đây là chuyện KIỂU, và kiểu là thứ duy nhất bắt lỗi lúc biên
// dịch thay vì lúc chạy.
export const bundleLocaleSchema = z.enum([...SUPPORTED_LOCALE_BUNDLE] as [BundleLocale, ...BundleLocale[]]);

export const meResponseSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  fullName: z.string().nullable(),
  // M2: avatarUrl phải là URL hợp lệ (https R2/CDN theo SD-1) khi có; null khi chưa đặt.
  avatarUrl: z.string().url().nullable(),
  // ISO-8601 string khi serialize qua JSON; null khi chưa verify email.
  emailVerifiedAt: z.string().datetime().nullable(),
  onboardingCompleted: z.boolean(),
  /**
   * Ngôn ngữ người dùng đã chọn. `null` = CHƯA chọn ⇒ client theo ngôn ngữ hệ thống/thiết bị,
   * worker render thông báo theo `system_settings.default_locale`.
   * KHÔNG có default ở đây: "chưa chọn" là một trạng thái có thật, không phải thiếu dữ liệu.
   */
  locale: bundleLocaleSchema.nullable(),
});

export type MeResponse = z.infer<typeof meResponseSchema>;

/** Giới hạn độ dài tên hiển thị (US-E3.F1-0016 / @UC0007 — invariant story ≤30 ký tự). */
export const FULL_NAME_MAX_LENGTH = 30;

/**
 * US-IDM.F3-0546 (@UC0007 @UC9510) — tên người: CHỮ, khoảng trắng, và hai dấu nối tên.
 *
 * 🔴 QĐ người dùng 26/08/2026: *"không cho gõ emoji, ký tự đặc biệt hay số — chỉ thuần tên thôi"*.
 *
 * Đây KHÔNG phải ràng buộc thẩm mỹ. `publicHandle` = `slugifyName(tên)` + shortId, mà slug bỏ hết
 * ký tự ngoài `[a-z0-9]`. Tên toàn emoji/dấu câu ⇒ **slug RỖNG** ⇒ handle còn trơ shortId: một
 * chuỗi 8 ký tự vô nghĩa mà UI vẫn dán nhãn "Mã công khai", và **không đảo ngược được** (đổi tên
 * lại thì chuỗi cũ có thể đã bị người khác chiếm).
 *
 * `\p{L}` = MỌI chữ cái Unicode ⇒ tiếng Việt có dấu, tiếng Trung/Nhật/Hàn, Cyrillic đều qua.
 * Cố ý KHÔNG dùng `[a-zA-Z]`: nó chặn đúng "Nguyễn" — tức chặn phần lớn người dùng thật.
 *
 * Hai ngoại lệ có lý do người-thật, không phải chiều lòng ký tự:
 *   · `'` — O'Brien, N'Guyen
 *   · `-` — Jean-Pierre, Anne-Marie
 * Cả hai đều xuất hiện trên giấy tờ ở VN (người nước ngoài, con lai) và đều cho slug KHÔNG rỗng.
 *
 * ⚠️ Đo trên `goppy-dev` TRƯỚC khi siết: 12/12 tên hiện có đều thuần chữ, 0 tên chứa chữ số.
 * Không có người dùng nào đang bị ràng buộc này chặn.
 */
export const PERSON_NAME_PATTERN = /^[\p{L}][\p{L} '-]*$/u;

export const PERSON_NAME_MESSAGE = "Tên chỉ gồm chữ cái, khoảng trắng và dấu ' hoặc -";

/**
 * Body cho PATCH /v1/me.
 * - onboardingCompleted OPTIONAL: client gửi `true` khi hoàn thành onboarding (AC4, slice 0014).
 * - fullName OPTIONAL (US-E3.F1-0016 / @UC0007): tên hiển thị. Trim trước rồi validate
 *   1..30 ký tự — rỗng/chỉ-khoảng-trắng → reject (AC3); >30 → reject (AC2). Đếm SAU trim.
 * - email KHÔNG có ở đây ⇒ `.strict()` reject (AC5: email bất biến qua endpoint profile).
 * - `.strict()` chặn field lạ tại biên (chống IDOR/over-post: KHÔNG nhận id/email/role từ body).
 */
export const patchMeRequestSchema = z
  .object({
    onboardingCompleted: z.boolean().optional(),
    // 🔴 review #1158 M6/M8: chuẩn hoá (NFKC + nháy cong `’` ⇒ `'`) TRƯỚC mọi phép kiểm, và chuỗi đã chuẩn hoá là thứ được LƯU — kiểm một
    // chuỗi mà lưu chuỗi khác là để ký tự thô lọt DB. "O’Neil" (bàn phím iOS) ⇒ "O'Neil"; tên Việt dạng tổ hợp ⇒ dạng dựng sẵn.
    fullName: z
      .preprocess(
        normalizePersonNameInput,
        z
          .string()
          .trim()
          .min(1, "Tên hiển thị không được để trống")
          .max(FULL_NAME_MAX_LENGTH, "Tên hiển thị tối đa 30 ký tự")
          // Chặn SAU `.trim()` và sau `.min/.max` ⇒ thông báo lỗi ưu tiên đúng thứ tự người dùng
          // sửa được: rỗng → quá dài → sai ký tự.
          .regex(PERSON_NAME_PATTERN, PERSON_NAME_MESSAGE)
          // 🔴 NAME-INPUT-PATTERN-SOCIAL (QĐ PO 03/10): `\p{L}` của PERSON_NAME_PATTERN gồm cả chữ bổ trợ `\p{Lm}` (U+02D0 `ː` trông như
          // dấu hai chấm) ⇒ "Phong ː Thủ quỹ ː Phong" lọt. Chốt ký tự CHUNG của tên người (`@goppy/domain` `person-name.ts`) đóng lỗ đó —
          // siết, không nới; khuôn chặt (chữ đầu, không số) giữ nguyên. Regex KHÔNG đổi chữ nào: bản Dart (`person_name.dart`) so nguyên
          // văn với nó.
          .refine((name) => !hasDisallowedPersonNameChars(name), PERSON_NAME_MESSAGE),
      )
      .optional(),
    /**
     * US-E24.F57-0520 (@UC9169) — BA trạng thái, không phải hai:
     *   · VẮNG field      ⇒ "đừng đụng lựa chọn hiện tại" (client chỉ đổi fullName)
     *   · `null`          ⇒ "bỏ chọn, cho tôi theo ngôn ngữ hệ thống"
     *   · `'vi'` / `'en'` ⇒ chọn tường minh
     * 🔴 `.nullable().optional()` — thiếu `.optional()` thì mọi lần đổi tên hiển thị sẽ âm thầm
     * XOÁ ngôn ngữ đã chọn; thiếu `.nullable()` thì không có đường "bỏ chọn" nào cả.
     */
    locale: bundleLocaleSchema.nullable().optional(),
  })
  .strict();

export type PatchMeRequest = z.infer<typeof patchMeRequestSchema>;
